import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { AttachedCvDocument, CvMatchResult, CvSearchResult, LockedCandidate } from "../types"

import {
  abortCvMatch,
  analyseDocForCvMatch,
  attachCvFromLibrary,
  type AttachedCvDocumentApi,
  classifyCvRequirements,
  type CvMatchResultApi,
  type CvSearchResultApi,
  deleteCvMatch,
  deleteCvMatchCandidate,
  deleteTenderDocument,
  getCvMatch,
  type LockedCandidateApi,
  searchCvLibrary,
  startCvMatch,
  type TenderCaseApi,
  updateLockedCandidates,
  uploadCvDocument,
} from "@/api/tender-api"

import { TENDER_QUERY_KEYS } from "./use-tender-cases"

const CV_MATCH_QUERY_KEYS = {
  cvMatch: (caseId: string, runId: string) =>
    [...TENDER_QUERY_KEYS.cvMatchPrefix, caseId, runId] as const,
}

function apiToResult(api: CvMatchResultApi): CvMatchResult | null {
  // "pending" after a reset means no data — the classify panel should show.
  // All other statuses (classified, processing, completed) return data so the
  // table can render with whatever role_pools are present. The section component
  // reads cvMatchStatus from the run doc to decide which state to show.
  if (api.status === "pending") return null
  return {
    cvLibraryId: api.cvLibraryId,
    rolePools: api.rolePools.map((pool) => ({
      roleId: pool.roleId,
      roleName: pool.roleName,
      quantityRequired: pool.quantityRequired,
      candidates: pool.candidates.map((c) => ({
        candidateId: c.candidateId,
        displayName: c.displayName,
        matchedRequirements: c.matchedRequirements,
        missingRequirements: c.missingRequirements,
        meritingMatches: c.meritingMatches,
        overqualifiedRequirements: c.overqualifiedRequirements,
        requirementEvidence: c.requirementEvidence,
        sourceSnippet: c.sourceSnippet,
        notes: c.notes,
        cvSource: c.cvSource,
      })),
      classifiedRequirements: pool.classifiedRequirements,
    })),
    lockedCandidates: api.lockedCandidates.map((lc) => ({
      roleId: lc.roleId,
      candidateId: lc.candidateId,
      displayName: lc.displayName,
      lockedAt: lc.lockedAt,
    })),
    error: api.error ?? undefined,
    failureReason: api.failureReason ?? undefined,
    cancelRequested: api.cancelRequested ?? undefined,
    rateLimitRetry: api.rateLimitRetry ?? undefined,
  }
}

function apiToSearchResult(api: CvSearchResultApi): CvSearchResult {
  return {
    libraryId: api.libraryId,
    sourcepage: api.sourcepage,
    sourcefile: api.sourcefile,
    snippet: api.snippet,
    score: api.score,
    isFirstChunkForFile: api.isFirstChunkForFile,
  }
}

function apiToAttachedDoc(api: AttachedCvDocumentApi): AttachedCvDocument {
  return {
    id: api.id,
    filename: api.filename,
    contentType: api.contentType,
    sizeKb: api.sizeKb,
    embeddingStatus: api.embeddingStatus,
    hasMarkdown: api.hasMarkdown,
    uploadedAt: api.uploadedAt,
  }
}

const POLL_INTERVAL_MS = 3000

/**
 * Load saved CV match results from Cosmos, polling every 3 seconds while processing.
 * Progressive results appear as each role completes in the background pipeline.
 */
type UseCvMatchResultOptions = {
  enabled?: boolean
  poll?: boolean
}

export function useCvMatchResult(
  caseId: string | undefined,
  runId: string | undefined,
  options?: UseCvMatchResultOptions
) {
  const enabled = options?.enabled ?? true
  const poll = options?.poll ?? true
  return useQuery({
    queryKey: CV_MATCH_QUERY_KEYS.cvMatch(caseId ?? "", runId ?? ""),
    queryFn: () => getCvMatch(caseId!, runId!),
    enabled: !!caseId && !!runId && enabled,
    select: (api: CvMatchResultApi) => ({ result: apiToResult(api), status: api.status }),
    retry: 1,
    // Completed results stay fresh for 60s; refetchInterval overrides while processing.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      if (!poll) return false
      const status = (query.state.data as { status?: string } | undefined)?.status
      return status === "processing" ? POLL_INTERVAL_MS : false
    },
  })
}

/**
 * Fire the CV matching pipeline as a background task (POST returns 202).
 * The query cache is seeded with the initial "processing" state so polling begins.
 * useStartCvMatch is now purely a trigger — results come via useCvMatchResult polling.
 */
export function useStartCvMatch(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ runId, cvLibraryId }: { runId: string; cvLibraryId: string }) => {
      const api = await startCvMatch(caseId!, runId, cvLibraryId)
      return api
    },
    onSuccess: (api, { runId }) => {
      // Store the raw API object — select() in useCvMatchResult transforms it
      queryClient.setQueryData(CV_MATCH_QUERY_KEYS.cvMatch(caseId ?? "", runId), api)
    },
    // Settled, not success: the request may have auto-joined the caller
    // server-side before failing, so case state must refresh either way.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useUpdateLockedCandidates(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      runId,
      lockedCandidates,
    }: {
      runId: string
      lockedCandidates: LockedCandidateApi[]
    }) => {
      const api = await updateLockedCandidates(caseId!, runId, lockedCandidates)
      return api
    },
    onSuccess: (api, { runId }) => {
      // Store the raw API object so the UI reflects the new locked list immediately.
      // Keeping the cache shape raw everywhere (matching useStartCvMatch /
      // useClassifyCvRequirements) lets useCvMatchResult's select transform it
      // correctly — storing the already-transformed result here would make select
      // double-transform and drop `status`, which status-dependent UI relies on.
      queryClient.setQueryData(CV_MATCH_QUERY_KEYS.cvMatch(caseId ?? "", runId), api)
    },
    // Settled, not success: locking is an auto-joining action like its
    // siblings, so case access state refreshes whether the write worked or
    // failed after the join persisted.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

/**
 * Mutation to reset CV match results in Cosmos so a fresh run can be started.
 * Preserves locked candidates. Removes the query cache entry so the UI shows
 * the trigger panel rather than reloading the old result on the next render.
 */
export function useResetCvMatch(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ runId }: { runId: string }) => {
      await deleteCvMatch(caseId!, runId)
    },
    onSuccess: (_data, { runId }) => {
      // Remove the cached result so the query doesn't refetch the old data.
      queryClient.removeQueries({ queryKey: CV_MATCH_QUERY_KEYS.cvMatch(caseId ?? "", runId) })
    },
    // Invalidate the case query so cvMatchStatus refreshes — on settle, since
    // a failed request may still have auto-joined the caller.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

export function useAbortCvMatch(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ runId }: { runId: string }) => {
      return abortCvMatch(caseId!, runId)
    },
    onSettled: (_data, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: CV_MATCH_QUERY_KEYS.cvMatch(caseId ?? "", runId),
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

/**
 * Mutation to run Pass 1 (requirement classification) for all roles.
 * On success, caches the raw API response so useCvMatchResult's select can transform it.
 */
export function useClassifyCvRequirements(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ runId }: { runId: string }) => {
      return classifyCvRequirements(caseId!, runId)
    },
    onSuccess: (api, { runId }) => {
      // Store the raw API object — select() in useCvMatchResult transforms it
      queryClient.setQueryData(CV_MATCH_QUERY_KEYS.cvMatch(caseId ?? "", runId), api)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

/**
 * Mutation to run Pass 2 (candidate analysis) for a single document.
 * On success, updates the query cache with the raw API response.
 */
export function useAnalyseDocForCvMatch(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      runId,
      docId,
      cvLibraryId,
      cvSource,
    }: {
      runId: string
      docId: string
      cvLibraryId: string
      cvSource?: string
    }) => {
      return analyseDocForCvMatch(caseId!, runId, { docId, cvLibraryId, cvSource })
    },
    onSuccess: (api, { runId }) => {
      // Store the raw API object — select() in useCvMatchResult transforms it
      queryClient.setQueryData(CV_MATCH_QUERY_KEYS.cvMatch(caseId ?? "", runId), api)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

/**
 * Mutation to remove a specific candidate from all role pools in the CV match result.
 * The document stays in storage — only the candidate rows are removed.
 */
export function useRemoveCandidate(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ runId, candidateId }: { runId: string; candidateId: string }) => {
      return deleteCvMatchCandidate(caseId!, runId, candidateId)
    },
    onSuccess: (api, { runId }) => {
      queryClient.setQueryData(CV_MATCH_QUERY_KEYS.cvMatch(caseId ?? "", runId), api)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

/**
 * Mutation to search a CV library for candidates matching a query.
 * Returns a list of matching CV chunks. Searching auto-joins open viewers
 * server-side, so the case query is refreshed to pick up the caller's new
 * role (the case page's access-source watcher then marks the participant
 * list stale).
 */
export function useCvLibrarySearch(caseId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      cvLibraryId,
      query,
      top,
    }: {
      cvLibraryId: string
      query: string
      top?: number
    }): Promise<CvSearchResult[]> => {
      const results = await searchCvLibrary(caseId!, { cvLibraryId, query, top })
      return results.map(apiToSearchResult)
    },
    onSettled: () => {
      // The auto-join persists before the search runs, so a failed search
      // (backend 5xx, bad library) can still have flipped the caller's role —
      // refresh the case on failure too, not just success.
      if (caseId) {
        void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
      }
    },
  })
}

/**
 * Mutation to attach a CV from a library into the tender case.
 * On success, invalidates the tender case query so cv_document_ids refreshes.
 */
export function useAttachCvFromLibrary(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      runId,
      cvLibraryId,
      sourcefile,
    }: {
      runId: string
      cvLibraryId: string
      sourcefile: string
    }): Promise<{ document: AttachedCvDocument; alreadyAttached: boolean }> => {
      const response = await attachCvFromLibrary(caseId!, runId, { cvLibraryId, sourcefile })
      return {
        document: apiToAttachedDoc(response.document),
        alreadyAttached: response.alreadyAttached,
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

/**
 * Mutation to upload a local CV file to the tender case as a cv document.
 * On success, updates the case cache so cvDocuments refreshes immediately.
 */
export function useUploadCvDocument(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ runId, file }: { runId: string; file: File }): Promise<TenderCaseApi> =>
      uploadCvDocument(caseId!, { runId, file }),
    onSuccess: (data) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId ?? ""), data)
    },
    onError: () => {
      // The request may have auto-joined the caller before failing.
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId ?? "") })
    },
  })
}

/**
 * Mutation to remove a CV document from a run.
 * On success, invalidates the case query so cvDocuments refreshes.
 */
export function useRemoveCvDocument(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ docId, runId }: { docId: string; runId: string }): Promise<TenderCaseApi> =>
      deleteTenderDocument(caseId!, docId, runId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.case(caseId ?? ""),
      })
    },
  })
}

export type { LockedCandidate, CvSearchResult, AttachedCvDocument }
